import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useBlocker } from "react-router";

import { Button, Modal } from "@/components/ui";

/**
 * The forms that are dirty right now. A router consults only the blocker registered last, and every mounted
 * form registers one (a closed dialog stays mounted), so each blocker answers for all of them rather than
 * for its own form only.
 */
const dirtyForms = new Map<string, { ignoreSearch: boolean }>();

/**
 * Warns before unsaved changes are lost (QA-22), for a form or a dialog that knows when it is dirty:
 *
 * - closing or refreshing the tab asks the browser to confirm (`beforeunload`);
 * - a move to another page, or another `?tab=`, in the app asks in a dialog;
 * - `guard(action)` runs `action` at once when clean, and otherwise only after the person chooses to discard.
 *   Wrap the ways a dialog closes (Close, Cancel, Escape, the scrim) in it. A save closes the dialog through
 *   the parent as before, and a form that has just saved is clean again, so neither asks.
 *
 * A dialog whose open state lives in the URL (`?modal=…`) passes `ignoreSearch`, so opening, closing or saving it
 * is not mistaken for leaving (the browser's Back button still asks); a page form leaves it off, so switching
 * `?tab=` asks.
 *
 * Render `modal` beside the form, outside any `<dialog>` it guards. Needs a data router (the app has one).
 */
export function useUnsavedChanges(
  isDirty: boolean,
  { ignoreSearch = false }: { ignoreSearch?: boolean } = {},
): {
  guard: (action: () => void) => void;
  modal: ReactNode;
} {
  const id = useId();
  const blocker = useBlocker(({ currentLocation, nextLocation, historyAction }) => {
    const pathChanged = currentLocation.pathname !== nextLocation.pathname;
    const searchChanged = currentLocation.search !== nextLocation.search;
    // A dialog's own open, close and save change the query string by push or replace; Back is the person leaving.
    return [...dirtyForms.values()].some(
      (form) => pathChanged || (searchChanged && (!form.ignoreSearch || historyAction === "POP")),
    );
  });
  const [closing, setClosing] = useState<(() => void) | null>(null);
  const keepEditingRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isDirty) return;
    dirtyForms.set(id, { ignoreSearch });
    return () => void dirtyForms.delete(id);
  }, [id, isDirty, ignoreSearch]);

  useEffect(() => {
    if (!isDirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = ""; // older browsers only show their prompt when this is set
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isDirty]);

  // Saved while the warning was up: there is nothing left to lose, so let the move through.
  useEffect(() => {
    if (blocker.state === "blocked" && dirtyForms.size === 0) blocker.proceed();
  }, [blocker, isDirty]);

  // Saved while a dialog close was waiting on an answer: drop the question (the dialog stays as it is).
  if (closing && !isDirty) setClosing(null);

  const guard = (action: () => void) => {
    if (isDirty) setClosing(() => action);
    else action();
  };

  const blocked = blocker.state === "blocked";
  const keepEditing = () => {
    setClosing(null);
    if (blocked) blocker.reset();
  };
  const discard = () => {
    if (blocked) blocker.proceed();
    else if (closing) {
      const action = closing;
      setClosing(null);
      action();
    }
  };

  const modal = (
    <Modal
      open={blocked || closing !== null}
      onClose={keepEditing}
      title="Leave without saving?"
      initialFocus={keepEditingRef}
      footer={
        <>
          <Button ref={keepEditingRef} type="button" variant="secondary" onClick={keepEditing}>
            Keep editing
          </Button>
          <Button type="button" variant="danger" onClick={discard}>
            Discard changes
          </Button>
        </>
      }
    >
      <p className="text-sm text-fg-muted">Your changes aren’t saved yet. Leaving now discards them.</p>
    </Modal>
  );

  return { guard, modal };
}
